/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX, ReactNode } from "react";
import { useEffect, useRef } from "react";

import {
  useUnityDispatch,
  useUnitySelector
} from "../../viewModel/UnityAppStore";
import {
  fetchGlobalEndpoints,
  fetchMsoDefaultBranding,
  fetchMsoDefaultStringOverrides,
  selectIsEndpointsFetched,
  selectIsBrandingFetched,
  selectIsStringOverridesFetched
} from "../../viewModel/appStartup";
import {
  initializeSignIn,
  rehydrateTokenFromStorage,
  resolveNextAuthStep,
  selectIsInitialized,
  selectIsAuthenticated,
  selectIsTokenRehydrating,
  selectAuthOrchestrationStatus
} from "../../viewModel/signIn";
import { initialiseVideoDebugOverlay } from "../../viewModel/videoDebugOverlay";
import { Log } from "../../model/logging";

interface AppInitializerProps {
  children: ReactNode;
}

/**
 * Root-level component that owns the startup dispatch sequence:
 *   1. fetchGlobalEndpoints + rehydrateTokenFromStorage (parallel on mount)
 *   2. fetchMsoDefaultBranding + fetchMsoDefaultStringOverrides (parallel,
 *      after endpoints are fetched)
 *   3. initializeSignIn (after BOTH branding and string overrides are
 *      fetched — mirrors unity-android's `ParallelPreSignInStrings`
 *      task group, where the sign-in phase waits on the whole group)
 *   4. resolveNextAuthStep (after sign-in init, when not rehydrating/authenticated)
 *
 * Renders children unconditionally — does NOT gate rendering on
 * startup completion.
 */
const AppInitializer: FunctionComponent<AppInitializerProps> = ({
  children
}): JSX.Element => {
  const TAG: string = "AppInitializer";

  const dispatch = useUnityDispatch();
  const isEndpointsFetched: boolean = useUnitySelector(
    selectIsEndpointsFetched
  );
  const isBrandingFetched: boolean = useUnitySelector(selectIsBrandingFetched);
  const isStringOverridesFetched: boolean = useUnitySelector(
    selectIsStringOverridesFetched
  );
  const isInitialized: boolean = useUnitySelector(selectIsInitialized);
  const isTokenRehydrating: boolean = useUnitySelector(
    selectIsTokenRehydrating
  );
  const isAuthenticated: boolean = useUnitySelector(selectIsAuthenticated);
  const authOrchestrationStatus: string = useUnitySelector(
    selectAuthOrchestrationStatus
  );
  const hasDispatched = useRef<boolean>(false);
  const hasDispatchedOrchestration = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController>(new AbortController());

  // Step 1 — Fetch global endpoints + rehydrate token in parallel
  useEffect((): (() => void) => {
    const controller = abortControllerRef.current;
    if (!hasDispatched.current) {
      hasDispatched.current = true;
      Log.d(
        TAG,
        "useEffect() dispatching fetchGlobalEndpoints + rehydrateTokenFromStorage"
      );
      // The restore runs in parallel; a token that needs refreshing waits on
      // this promise so its calls resolve against a loaded endpoint catalog.
      const endpointsReady: Promise<unknown> = dispatch(
        fetchGlobalEndpoints({ signal: controller.signal })
      );
      dispatch(rehydrateTokenFromStorage({ endpointsReady: endpointsReady }));
    }
    return (): void => {
      controller.abort();
    };
  }, [dispatch]);

  // Step 1b — Restore the Video Debug Overlay preference and resolve whether
  // this session may see restricted metrics. Independent of everything else
  // in the startup sequence: it reads local storage only, and the overlay
  // must be able to come up before sign-in so a playback fault during
  // startup is still diagnosable.
  useEffect((): void => {
    dispatch(initialiseVideoDebugOverlay());
  }, [dispatch]);

  // Step 2 — Once endpoints are fetched, fetch branding + string overrides
  // in parallel (same device-configuration-service, two independent
  // endpoints — neither blocks the other).
  useEffect((): (() => void) => {
    if (isEndpointsFetched) {
      Log.d(
        TAG,
        "useEffect() endpoints ready, dispatching fetchMsoDefaultBranding + " +
          "fetchMsoDefaultStringOverrides"
      );
      dispatch(fetchMsoDefaultBranding());
      dispatch(fetchMsoDefaultStringOverrides());
    }
    return (): void => {
      // No cleanup required
    };
  }, [dispatch, isEndpointsFetched]);

  // Step 3 — Once branding AND string overrides are both fetched,
  // initialize sign-in
  useEffect((): (() => void) => {
    if (isBrandingFetched && isStringOverridesFetched) {
      Log.d(
        TAG,
        "useEffect() branding + string overrides ready, dispatching " +
          "initializeSignIn"
      );
      dispatch(initializeSignIn({ signal: abortControllerRef.current.signal }));
    }
    return (): void => {
      // No cleanup required
    };
  }, [dispatch, isBrandingFetched, isStringOverridesFetched]);

  // Step 4 — Once sign-in is initialized and token is not being
  // rehydrated, kick off the auth orchestration (silent IP auto-login
  // walk). Skip when already authenticated (rehydrate succeeded) or
  // when the orchestration has already moved past "idle".
  useEffect((): (() => void) => {
    if (
      isInitialized &&
      !isTokenRehydrating &&
      !isAuthenticated &&
      !hasDispatchedOrchestration.current
    ) {
      hasDispatchedOrchestration.current = true;
      Log.d(TAG, "useEffect() dispatching resolveNextAuthStep");
      dispatch(resolveNextAuthStep());
    }
    return (): void => {
      // No cleanup required
    };
  }, [dispatch, isInitialized, isTokenRehydrating, isAuthenticated]);

  // Step 4b — Re-dispatch resolveNextAuthStep when an IP auto-login
  // attempt failed with a fallback-eligible error (status goes back to
  // "resolving" from the rejected extra reducer).
  useEffect((): (() => void) => {
    if (
      authOrchestrationStatus === "resolving" &&
      hasDispatchedOrchestration.current
    ) {
      Log.d(TAG, "useEffect() re-dispatching resolveNextAuthStep (fallback)");
      dispatch(resolveNextAuthStep());
    }
    return (): void => {
      // No cleanup required
    };
  }, [dispatch, authOrchestrationStatus]);

  return <>{children}</>;
};

export default AppInitializer;
